<template>
  <div>
    <example-payload-view/>
    <upload-drop-zone
        :ref="dropzoneId"
        :accepted-file-types="allowedFormats"
        :asset-id="assetId"
        :auto-update-preview="false"
        :auto-upload="false"
        :downloadable="false"
        :form-id="dropzoneId"
        :image-editable="true"
        :is-loading="isLoading"
        :removable="false"
        asset-type="assembly"
        class="meta-data-upload mt-2"
        target-type="Asset"
        @data="setColumnNames"
        @fileReceived="fileReceived"
        @queueEmpty="queueEmpty"
    >
      <div class="mt-3">
        {{ $t("uploadMetaFile") }}
      </div>
    </upload-drop-zone>

    <div v-if="error" class="form-error mt-2">
      {{ error }}
    </div>
    <div v-if="metaDataColumns && metaDataColumns.length">
      <label class="lighter mt-3 mb-0">1 {{ $t("PostProcessingForMeta") }}</label><br/>
      <drop-down
          :initial-active-value="vhubPreProcessor"
          :labels="metaDataLabels"
          :values="metaDataValues"
          class="w-100"
          @select="setVhubPreProcessor"
      />
    </div>
    <div v-if="vhubPreProcessor">
      <div class="mt-2">
        <label class="lighter mb-0">2 {{ $t("selectIdentifier") }}</label><br/>
        <drop-down
            :labels="metaDataColumns"
            :values="metaDataColumns"
            class="w-100"
            @select="setPartIdentifierColumn"
        />
      </div>
    </div>
  </div>
</template>

<script>
import UploadDropZone from "@/components/files/UploadDropZone.vue";
import DataProcessor from "@/components/fileProcessors/DataProcessor";
import {decodeFileContent, sleep} from "@/utils";
import {metaDataFileFormats} from "@/enum";
import ExamplePayloadView from "@/components/assemblies/ExamplePayloadView.vue";
import DropDown from "@/components/forms/DropDown.vue";

export default {
  name: "MetaDataUpload",
  components: {DropDown, UploadDropZone, ExamplePayloadView},
  props: {
    assetId: {type: String, required: true},
    dropzoneId: {type: String, default: 'metaDataUpload'},
    allowJson: {type: Boolean, default: false},
    validationMethodsForColNames: {type: Array, default: () => []},
    validationMethodsForData: {type: Array, default: () => []},
  },
  data() {
    return {
      fileContent: '',
      metaDataColumns: [],
      metaDataFileName: '',
      isLoading: false,
      metaConfig: {},
      vhubPreProcessor: '',
      error: '',
      dataProcessor: null,
      metaDataFileFormats,
      allowedFormats: [],
      partIdentifierColumn: null
    };
  },
  computed: {
    metaDataLabels() {
      return Object.values(this.metaDataFileFormats).map((key) => {
        return this.$t(key.name)
      })
    },
    metaDataValues() {
      return Object.keys(this.metaDataFileFormats);
    }
  },
  watch: {
    vhubPreProcessor: {
      handler: function () {
        this.$emit('setVhubPreProcessor', this.vhubPreProcessor);
      },
    }
  },
  beforeMount() {
    this.setAllowedFormats();
  },
  methods: {
    setAllowedFormats() {
      let items = JSON.parse(JSON.stringify(this.metaDataFileFormats));
      if (!this.allowJson) {
        delete items.JSONParser;
      }
      this.allowedFormats = Object.values(items).map((item) => {
        return '.' + item.format;
      }).join(',')
    },
    setVhubPreProcessor(value) {
      this.vhubPreProcessor = value;
    },
    setPartIdentifierColumn(value) {
      this.partIdentifierColumn = value;
      this.$emit('partIdentifierColumn', value);
    },
    resumeUpload() {
      this.$refs[this.dropzoneId].resumeUpload();
    },
    setColumnNames(data, file) {
      // retrieves the column names
      this.fileContent = data;
      const {name} = file;
      this.autoSetParser(name);
    },
    fileReceived(assetId, fileName) {
      this.$emit('fileReceived', assetId, fileName);
    },
    queueEmpty() {
      this.fileContent = null;
      this.metaConfig = {};
      this.metaDataColumns = [];
      this.vhubPreProcessor = null;
      this.$emit('reset');
    },
    async parseData() {
      this.error = '';
      this.metaDataColumns = [];
      this.metaConfig = {};
      this.dataProcessor = new DataProcessor(this.vhubPreProcessor);
      await this.dataProcessor.init();
      const file = decodeFileContent(this.fileContent);
      this.metaDataColumns = await this.dataProcessor.getColumnNames(file, (error) => {
        this.error = error;
      })
      if(this.vhubPreProcessor === 'genericXMLParser' && this.error) {
        this.vhubPreProcessor = 'xmlWindchillSearchRes';
        return await this.parseData();
      }
      for (let i = 0; i < this.validationMethodsForColNames.length; i++) {
        const method = this.validationMethodsForColNames[i];
        method(this.metaDataColumns);
      }
      this.$emit('metaDataColumns', this.metaDataColumns);
    },
    async autoSetParser(fileName) {
      await sleep(300);
      if (fileName.toLowerCase().includes('.csv')) {
        this.vhubPreProcessor = 'csvParser';
      } else if (fileName.toLowerCase().includes('.json')) {
        this.vhubPreProcessor = 'JSONParser';
      } else {
        this.vhubPreProcessor = 'genericXMLParser';
      }
      if (this.vhubPreProcessor) {
        await this.parseData();
        if (this.error) {
          this.vhubPreProcessor = null;
        }
      }
    },

  }
}
</script>

<style lang="scss" scoped>
.meta-data-upload {
  max-width: 400px;
}
</style>